/**
 * Debug utility for controlling debug logging across the application
 *
 * Usage:
 * - Set NEXT_PUBLIC_DEBUG_ENABLED=true in .env.local to enable debug logging
 * - Set NEXT_PUBLIC_DEBUG_ENABLED=false or omit to disable debug logging
 * - Use debug.log() instead of console.log() for debug messages
 */

import { logger } from "../../logger";

const isDebugEnabled = process.env.NEXT_PUBLIC_DEBUG_ENABLED === "true";

export const debug = {
  /**
   * Log debug messages only when debug mode is enabled
   */
  log: (...args: unknown[]) => {
    if (isDebugEnabled) {
      logger.debug("debug", { args });
    }
  },

  /**
   * Log auth-specific debug messages
   */
  auth: (...args: unknown[]) => {
    if (isDebugEnabled) {
      logger.debug("AUTH DEBUG", { args });
    }
  },

  /**
   * Log redirect-specific debug messages
   */
  redirect: (...args: unknown[]) => {
    if (isDebugEnabled) {
      logger.debug("REDIRECT DEBUG", { args });
    }
  },

  /**
   * Log API-specific debug messages
   */
  api: (...args: unknown[]) => {
    if (isDebugEnabled) {
      logger.debug("API DEBUG", { args });
    }
  },

  /**
   * Log schema-specific debug messages
   */
  schema: (...args: unknown[]) => {
    if (isDebugEnabled) {
      logger.debug("SCHEMA DEBUG", { args });
    }
  },

  /**
   * Check if debug mode is enabled
   */
  isEnabled: () => isDebugEnabled,

  /**
   * Get debug status for display purposes
   */
  getStatus: () => ({
    enabled: isDebugEnabled,
    envVar: process.env.NEXT_PUBLIC_DEBUG_ENABLED,
  }),
};
